import { DiscountCode } from "../../utils/firestore/getDiscounts";
import {
  ActionWithPayload,
  createAction,
  withMatcher,
} from "../../utils/reducer/reducer.utils";
import { Customer } from "../../utils/types";
import { Dishes } from "../kitchen/kitchen.types";
import {
  CartItem,
  CART_ACTION_TYPES,
  CustomerAccountCache,
  CustomerDetails,
  DeliveryAddress,
} from "./cart.types";

const addCartItem = (
  cartItems: CartItem[],
  productToAdd: Dishes,
  quantity: number,
  variantSelected: [],
  dishPrice: number,
  dishPriceWithVariants: number,
  note: string | null = null
): CartItem[] => {
  let randomId = Math.random().toString(36).substring(7);
  if (process.env.NODE_ENV !== "production") {
    console.log("[variants][cart-action:addCartItem]", {
      product: productToAdd?.dishName,
      quantity,
      variantSelected,
    });
  }

  //find if cart items contains product to add
  // const existingCartItem = cartItems.find(
  //   (cartItem) => cartItem.id === productToAdd.id
  // );

  // If found increment quantity
  // if (existingCartItem) {
  // return cartItems.map((cartItem) =>
  //   cartItem.id === productToAdd.id
  //     ? { ...cartItem, quantity: cartItem.quantity + quantity, dishPrice: dishPriceVariant, variantSelected: variantSelected }
  //     : cartItem
  // );
  // }
  // return new array with modified cartitems/new cart item
  return [
    ...cartItems,
    {
      ...productToAdd,
      quantity: quantity,
      dishPrice: dishPrice,
      dishPriceWithVariants: dishPriceWithVariants,
      variantSelected: variantSelected,
      note,
      cartItemId: randomId,
    },
  ];
};

// Add 1 extra item to cart from the checkout page
const incrementCartItem = (
  cartItems: CartItem[],
  productToAdd: Dishes
): CartItem[] => {
  //find if cart items contains product to add
  const existingCartItem = cartItems.find(
    (cartItem) => cartItem.cartItemId === productToAdd.cartItemId
  );

  // If found increment quantity
  if (existingCartItem) {
    return cartItems.map((cartItem) =>
      cartItem.cartItemId === productToAdd.cartItemId
        ? { ...cartItem, quantity: cartItem.quantity + 1 }
        : cartItem
    );
  }
  // return new array with modified cartitems/new cart item
  return [...cartItems, { ...productToAdd, quantity: 1 }];
};

const removeCartItem = (
  cartItems: CartItem[],
  cartItemToRemove: CartItem
): CartItem[] => {
  // find the cart item to remove
  const existingCartItem = cartItems.find(
    (cartItem) => cartItem.cartItemId === cartItemToRemove.cartItemId
  );

  // check if qty = 1 ? remove from cart
  if (existingCartItem && existingCartItem.quantity === 1) {
    return cartItems.filter(
      (cartItem) => cartItem.cartItemId !== cartItemToRemove.cartItemId
    );
  }
  // return cart item with reduced qty
  return cartItems.map((cartItem) =>
    cartItem.cartItemId === cartItemToRemove.cartItemId
      ? { ...cartItem, quantity: cartItem.quantity - 1 }
      : cartItem
  );
};

const clearCartItem = (
  cartItems: CartItem[],
  cartItemToClear: CartItem
): CartItem[] => {
  return cartItems.filter(
    (cartItem) => cartItem.cartItemId !== cartItemToClear.cartItemId
  );
};

export type SetCartItems = ActionWithPayload<
  CART_ACTION_TYPES.SET_CART_ITEMS,
  CartItem[]
>;

export type SetOrderNote = ActionWithPayload<
  CART_ACTION_TYPES.SET_ORDER_NOTE,
  string
>;

export type SetCustomerDetails = ActionWithPayload<
  CART_ACTION_TYPES.SET_CUSTOMER_DETAILS,
  CustomerDetails
>;

export type SetDiscount = ActionWithPayload<
  CART_ACTION_TYPES.SET_DISCOUNT,
  DiscountCode
>;

export type SetNewCustomer = ActionWithPayload<
  CART_ACTION_TYPES.SET_NEW_CUSTOMER,
  Customer
>;


export type SetScheduledTime = ActionWithPayload<
  CART_ACTION_TYPES.SET_SCHEDULED_TIME,
  Date | string | null
>;

export type SetCustomerAccountCache = ActionWithPayload<
  CART_ACTION_TYPES.SET_CUSTOMER_ACCOUNT_CACHE,
  CustomerAccountCache
>;

export type SetCartKitchenIds = ActionWithPayload<
  CART_ACTION_TYPES.SET_CART_KITCHEN_ID,
  string
>;

export type SetPaymentProcessing = ActionWithPayload<
  CART_ACTION_TYPES.SET_PAYMENT_PROCESSING,
  boolean
>;

export type SetDeliveryAddress = ActionWithPayload<
  CART_ACTION_TYPES.SET_DELIVERY_ADDRESS,
  DeliveryAddress | null
>;

export const setCartItems = withMatcher(
  (cartItems: CartItem[]): SetCartItems =>
    createAction(CART_ACTION_TYPES.SET_CART_ITEMS, cartItems)
);

export const setOrderNote = withMatcher(
  (orderNote: string): SetOrderNote =>
    createAction(CART_ACTION_TYPES.SET_ORDER_NOTE, orderNote)
);

export const setCustomerDetails = withMatcher(
  (customerDetails: CustomerDetails): SetCustomerDetails =>
    createAction(CART_ACTION_TYPES.SET_CUSTOMER_DETAILS, customerDetails)
);

export const setDiscount = withMatcher(
  (discount: DiscountCode): SetDiscount =>
    createAction(CART_ACTION_TYPES.SET_DISCOUNT, discount)
);

export const clearDiscount = withMatcher(
  (): { type: typeof CART_ACTION_TYPES.CLEAR_DISCOUNT; payload: null } =>
    createAction(CART_ACTION_TYPES.CLEAR_DISCOUNT, null)
);

export const setNewCustomer = withMatcher(
  (customer: Customer): SetNewCustomer =>
    createAction(CART_ACTION_TYPES.SET_NEW_CUSTOMER, customer)
);

export const clearNewCustomer = withMatcher(
  (): { type: typeof CART_ACTION_TYPES.CLEAR_NEW_CUSTOMER; payload: null } =>
    createAction(CART_ACTION_TYPES.CLEAR_NEW_CUSTOMER, null)
);

export const setCustomerAccountCache = withMatcher(
  (cache: CustomerAccountCache): SetCustomerAccountCache =>
    createAction(CART_ACTION_TYPES.SET_CUSTOMER_ACCOUNT_CACHE, cache)
);

export const clearCustomerAccountCache = withMatcher(
  (): {
    type: typeof CART_ACTION_TYPES.CLEAR_CUSTOMER_ACCOUNT_CACHE;
    payload: null;
  } => createAction(CART_ACTION_TYPES.CLEAR_CUSTOMER_ACCOUNT_CACHE, null)
);

export const setScheduledTime = withMatcher(
  (scheduledTime: Date | string | null): SetScheduledTime =>
    createAction(CART_ACTION_TYPES.SET_SCHEDULED_TIME, scheduledTime)
);

export const setCartKitchenId = withMatcher(
  (kitchenId: string): SetCartKitchenIds =>
    createAction(CART_ACTION_TYPES.SET_CART_KITCHEN_ID, kitchenId)
);

export const setPaymentProcessing = withMatcher(
  (isPaymentProcessing: boolean): SetPaymentProcessing =>
    createAction(CART_ACTION_TYPES.SET_PAYMENT_PROCESSING, isPaymentProcessing)
);

export const setDeliveryAddress = withMatcher(
  (deliveryAddress: DeliveryAddress | null): SetDeliveryAddress =>
    createAction(CART_ACTION_TYPES.SET_DELIVERY_ADDRESS, deliveryAddress)
);

// Add the kitchenID to the cart so we can clear it when visiting different restaurants
export const addKitchenIdToCart = (kitchenId: string) => {
  return setCartKitchenId(kitchenId);
};

export const addItemToCart = (
  cartItems: CartItem[],
  productToAdd: Dishes,
  quantity: number,
  variantSelected: [],
  dishPrice: number,
  dishPriceWithVariants: number,
  note: string | null = null
) => {
  const newCartItems = addCartItem(
    cartItems,
    productToAdd,
    quantity,
    variantSelected,
    dishPrice,
    dishPriceWithVariants,
    note
  );
  return setCartItems(newCartItems);
};

export const incrementItemInCart = (
  cartItems: CartItem[],
  productToAdd: Dishes
) => {
  const newCartItems = incrementCartItem(cartItems, productToAdd);
  return setCartItems(newCartItems);
};

export const removeItemFromCart = (
  cartItems: CartItem[],
  cartItemToRemove: CartItem
) => {
  const newCartItems = removeCartItem(cartItems, cartItemToRemove);
  return setCartItems(newCartItems);
};

export const clearItemFromCart = (
  cartItems: CartItem[],
  cartItemToClear: CartItem
) => {
  const newCartItems = clearCartItem(cartItems, cartItemToClear);
  return setCartItems(newCartItems);
};

export const clearCart = () => {
  const emptyCart = [];
  return setCartItems(emptyCart);
};

